import type { Scene } from "@babylonjs/core/scene";
import type { Camera } from "@babylonjs/core/Cameras/camera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { DepthOfFieldEffectBlurLevel } from "@babylonjs/core/PostProcesses/depthOfFieldEffect";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";

/**
 * The game's look ("2d", the default; D33): the HD-2D atmosphere of Octopath
 * Traveler on top of the same arena. Warm low sun with shadows, torches
 * with flickering light and embers, dust motes, bloom, a tilt-shift depth of
 * field focused on the local fighter, vignette and a little grain.
 *
 * Costs GPU time (post-processing, shadows, 6 lights): to be measured on
 * phones before keeping it.
 */
export interface Hd2d {
  setDiorama(enabled:boolean):void;
  pulse(strength: number): void;
  update(dt: number, focus: Vector3 | null): void;
  /** Lights or puts out one torch (a brazier broken and back), by its index in `Hd2dWorld.torches`. */
  setTorchLit(index: number, lit: boolean): void;
  /** Post-processing and shadows go with the arena on a change of map (D49); the rest is `stage`'s. */
  dispose(): void;
  /** The camera is in the museum (D52): its night instead of the arena's mood, and back. */
  setMuseum(inMuseum: boolean): void;
}

/** What the atmosphere needs from the arena's meshes: who casts and receives shadows, where the torches burn. */
export interface Hd2dWorld {
  casters: Mesh[];
  receivers: Mesh[];
  /** Torch flames, in world space. */
  torches: Vector3[];
  /** Half size of the playing area on X and Z, for the dust motes. */
  extent: { x: number; z: number };
  /** "dusk" (default): warm late afternoon. "inferno": red haze, embers rising everywhere. */
  mood?: "dusk" | "inferno";
  /** The torch fires are drawn by the arena itself (braziers): only lights and embers here. */
  fireDrawn?: boolean;
}

interface Torch {
  light: PointLight;
  embers: ParticleSystem;
  base: number;
  phase: number;
  lit: boolean;
}

/** Colours of a mood. */
const MOODS = {
  dusk: {
    clear: "#2e2036ff", fog: "#4a3240", fogStart: 26, fogEnd: 70, ambient: [0.46, 0.38, 0.36],
    sky: "#e8d4d8", skyIntensity: 0.85, ground: "#4a3430", sun: "#ffc88e", sunIntensity: 1.2,
    motes: [new Color4(1, 0.9, 0.65, 0.55), new Color4(0.85, 0.8, 1, 0.4)], motesRise: 0.35, motesRate: 22,
    bloomThreshold: 0.62, bloomWeight: 0.45, exposure: 1.45, contrast: 1.15, torch: 0.9, torchRange: 8,
  },
  inferno: {
    // Readability first (D39): the haze only in the distance, a near-white key light so the colours
    // of objects and fighters survive, the red coming from the fires and the ground's embers.
    // Lit like a furnace: strong warm key light, lava glowing from below, a burning sky.
    clear: "#4a1608ff", fog: "#6a240e", fogStart: 40, fogEnd: 120, ambient: [0.66, 0.52, 0.46],
    sky: "#ffd8b4", skyIntensity: 1.05, ground: "#6a2410", sun: "#ffd09a", sunIntensity: 1.8,
    motes: [new Color4(1, 0.65, 0.25, 0.9), new Color4(1, 0.4, 0.12, 0.7)], motesRise: 1.1, motesRate: 40,
    bloomThreshold: 0.7, bloomWeight: 0.42, exposure: 1.6, contrast: 1.25, torch: 1.6, torchRange: 11,
  },
  museum: {
    // The museum at night (D52): moonlight through the windows, a deep blue sky, the candles' warmth
    // left to the bloom. Fighters stay readable: a fair ambient, a cool but bright key light.
    clear: "#0b1230ff", fog: "#141c3c", fogStart: 40, fogEnd: 110, ambient: [0.52, 0.52, 0.64],
    sky: "#d4dcff", skyIntensity: 0.8, ground: "#2a2234", sun: "#c8d4ff", sunIntensity: 1.1,
    motes: [new Color4(0.85, 0.9, 1, 0.5), new Color4(1, 0.9, 0.7, 0.35)], motesRise: 0.25, motesRate: 18,
    bloomThreshold: 0.75, bloomWeight: 0.3, exposure: 1.25, contrast: 1.15, torch: 0.9, torchRange: 8,
  },
} as const;
type Mood = (typeof MOODS)[keyof typeof MOODS];

export function setupHd2d(scene: Scene, camera: Camera, world: Hd2dWorld): Hd2d {
  const arenaMood: Mood = MOODS[world.mood ?? "dusk"];
  let mood: Mood = arenaMood;
  const sky = scene.getLightByName("sky") as HemisphericLight | null;
  const sun = scene.getLightByName("sun") as DirectionalLight | null;
  /** Sky, fog and lights of a mood; the post-processing's share is set where the pipeline is. */
  const lightUp = (m: Mood): void => {
    scene.clearColor = Color4.FromHexString(m.clear);
    scene.fogColor = Color3.FromHexString(m.fog);
    scene.fogStart = m.fogStart;
    scene.fogEnd = m.fogEnd;
    scene.ambientColor = new Color3(m.ambient[0], m.ambient[1], m.ambient[2]);
    if (sky) {
      sky.intensity = m.skyIntensity;
      sky.diffuse = Color3.FromHexString(m.sky);
      sky.groundColor = Color3.FromHexString(m.ground);
    }
    if (sun) {
      sun.diffuse = Color3.FromHexString(m.sun);
      sun.intensity = m.sunIntensity;
    }
  };
  lightUp(mood);
  let shadows: ShadowGenerator | null = null;
  if (sun) {
    sun.direction = new Vector3(-0.55, -0.62, 0.55);
    sun.position = new Vector3(14, 18, -14);
    shadows = new ShadowGenerator(1024, sun);
    shadows.usePercentageCloserFiltering = true;
    shadows.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
    shadows.darkness = 0.45;
    for (const mesh of world.casters) shadows.addShadowCaster(mesh);
    for (const mesh of world.receivers) mesh.receiveShadows = true;
  }

  const torches: Torch[] = [];
  const flameMaterial = new StandardMaterial("flame", scene);
  flameMaterial.disableLighting = true;
  flameMaterial.emissiveColor = Color3.FromHexString("#ffb347");
  const sconceMaterial = new StandardMaterial("sconce", scene);
  sconceMaterial.diffuseColor = Color3.FromHexString("#3a2a22");
  sconceMaterial.specularColor = Color3.Black();
  const spark = dotTexture(scene);
  for (const { x, y: flameY, z } of world.torches) {
    const y = flameY - 0.3;
    if (!world.fireDrawn) {
      const sconce = CreateBox("sconce", { width: 0.16, height: 0.3, depth: 0.16 }, scene);
      sconce.position.set(x, y, z);
      sconce.material = sconceMaterial;
      const flame = CreateSphere("flame", { diameter: 0.22, segments: 4 }, scene);
      flame.position.set(x, y + 0.24, z);
      flame.scaling.y = 1.5;
      flame.material = flameMaterial;
    }

    const light = new PointLight("torch", new Vector3(x, y + 0.3, z), scene);
    light.diffuse = Color3.FromHexString("#ff9a3c");
    light.specular = Color3.Black();
    light.range = mood.torchRange;
    light.intensity = mood.torch;

    const embers = new ParticleSystem("embers", 40, scene);
    embers.particleTexture = spark;
    embers.emitter = new Vector3(x, y + 0.3, z);
    embers.minEmitBox = new Vector3(-0.06, 0, -0.06);
    embers.maxEmitBox = new Vector3(0.06, 0.05, 0.06);
    embers.color1 = new Color4(1, 0.75, 0.3, 1);
    embers.color2 = new Color4(1, 0.45, 0.15, 1);
    embers.colorDead = new Color4(0.4, 0.1, 0.05, 0);
    embers.minSize = 0.04;
    embers.maxSize = 0.09;
    embers.minLifeTime = 0.5;
    embers.maxLifeTime = 1.3;
    embers.emitRate = 14;
    embers.direction1 = new Vector3(-0.3, 1, -0.3);
    embers.direction2 = new Vector3(0.3, 1.6, 0.3);
    embers.minEmitPower = 0.4;
    embers.maxEmitPower = 0.9;
    embers.gravity = new Vector3(0, 0.4, 0);
    embers.blendMode = ParticleSystem.BLENDMODE_ADD;
    embers.start();
    torches.push({ light, embers, base: mood.torch, phase: Math.random() * 10, lit: true });
  }

  // Dust motes drifting in the light over the whole arena.
  const motes = new ParticleSystem("motes", 160, scene);
  motes.particleTexture = spark;
  motes.emitter = new Vector3(0, 0.3, 0);
  motes.minEmitBox = new Vector3(-world.extent.x, 0, -world.extent.z);
  motes.maxEmitBox = new Vector3(world.extent.x, 3, world.extent.z);
  motes.color1 = mood.motes[0];
  motes.color2 = mood.motes[1];
  motes.colorDead = new Color4(1, 0.9, 0.7, 0);
  motes.minSize = 0.03;
  motes.maxSize = 0.07;
  motes.minLifeTime = 4;
  motes.maxLifeTime = 8;
  motes.emitRate = mood.motesRate;
  motes.direction1 = new Vector3(-0.1, 0.15, -0.1);
  motes.direction2 = new Vector3(0.1, mood.motesRise, 0.1);
  motes.minEmitPower = 0.2;
  motes.maxEmitPower = 0.4;
  motes.blendMode = ParticleSystem.BLENDMODE_ADD;
  motes.preWarmCycles = 200;
  motes.start();

  // Post-processing: bloom, tilt-shift depth of field, colour grading, vignette, grain.
  const pipeline = new DefaultRenderingPipeline("hd2d", true, scene, [camera]);
  pipeline.samples = 1;
  pipeline.bloomEnabled = true;
  pipeline.bloomThreshold = mood.bloomThreshold;
  pipeline.bloomWeight = mood.bloomWeight;
  pipeline.bloomKernel = 48;
  pipeline.bloomScale = 0.5;
  pipeline.depthOfFieldEnabled = true;
  pipeline.depthOfFieldBlurLevel = DepthOfFieldEffectBlurLevel.Medium;
  pipeline.depthOfField.fStop = 0.9;
  pipeline.depthOfField.focalLength = 180;
  pipeline.depthOfField.lensSize = 130;
  pipeline.imageProcessingEnabled = true;
  const ip = pipeline.imageProcessing;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  ip.exposure = mood.exposure;
  ip.contrast = mood.contrast;
  ip.vignetteEnabled = true;
  ip.vignetteWeight = 1.25;
  ip.vignetteStretch = 0.4;
  ip.vignetteColor = new Color4(0.08, 0.04, 0.12, 0);
  pipeline.grainEnabled = true;
  pipeline.grain.intensity = 2.5;
  pipeline.grain.animated = true;

  let time = 0;
  let impulse = 0;
  const focusInView=new Vector3();
  let focusMm=0;
  return {
    setDiorama(enabled){pipeline.depthOfFieldEnabled=enabled;},
    pulse(strength) { impulse = Math.max(impulse, Math.min(1, strength)); },
    update(dt, focus) {
      time += dt;
      impulse = Math.max(0, impulse - dt * 2);
      ip.exposure = mood.exposure * (1 + impulse * 0.12);
      for (const t of torches) {
        t.light.intensity = t.lit ? t.base * (0.85 + impulse * 0.8 + 0.1 * Math.sin(time * 11 + t.phase) + 0.06 * Math.sin(time * 23 + t.phase * 2)) : 0;
      }
      if (focus) {
        // The depth buffer measures view-space Z, not Euclidean distance.
        // Scale the virtual aperture with framing: overview should keep the
        // same miniature look, while the fighter remains on the focus plane.
        Vector3.TransformCoordinatesToRef(focus,camera.getViewMatrix(),focusInView);
        const wanted=Math.max(1000,focusInView.z*1000);
        focusMm=focusMm===0 || Math.abs(wanted-focusMm)>5000 ? wanted : focusMm+(wanted-focusMm)*(1-Math.exp(-dt*10));
        pipeline.depthOfField.focusDistance=focusMm;
        pipeline.depthOfField.lensSize=.9*(focusMm-180)/180*1.0;
      }
      if (shadows && sun && focus) {
        // Keep the shadow map centred on the action.
        sun.position.set(focus.x + 14, 18, focus.z - 14);
      }
    },
    setTorchLit(index, lit) {
      const t = torches[index];
      if (!t || t.lit === lit) return;
      t.lit = lit;
      if (lit) t.embers.start();
      else t.embers.stop();
    },
    dispose() {
      pipeline.dispose();
      shadows?.dispose();
    },
    setMuseum(inMuseum) {
      const next = inMuseum ? MOODS.museum : arenaMood;
      if (next === mood) return;
      mood = next;
      lightUp(mood);
      pipeline.bloomThreshold = mood.bloomThreshold;
      pipeline.bloomWeight = mood.bloomWeight;
      ip.contrast = mood.contrast;
      motes.emitRate = inMuseum ? 0 : mood.motesRate;
    },
  };
}

/** A soft round dot for particles. */
function dotTexture(scene: Scene): DynamicTexture {
  const size = 16;
  const texture = new DynamicTexture("dot", { width: size, height: size }, scene, false);
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.5, "rgba(255,255,255,0.6)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  texture.update(false);
  texture.hasAlpha = true;
  return texture;
}
